/**
 * printRakezActivity — Opens a new tab with a print-ready, brand-styled HTML
 * page for a single RAKEZ business activity, then triggers window.print()
 * so the user can "Save as PDF" from the native browser dialog.
 *
 * Feb 2026 — Built after user's request. Mirrors the CloudGM FZ-LLC brand
 * used in `printAssignedTask.js`, with an additional RAKEZ logo in the
 * header (dual-logo layout) since the activity belongs to the RAKEZ
 * authority.
 *
 * Usage:
 *   import { printRakezActivity } from '../../utils/printRakezActivity';
 *   printRakezActivity(activity, { language: 'ar' | 'en' });
 */

// Absolute URLs — we serve the print HTML from a `blob:` URL, so relative
// paths resolve against `blob://...` and fail. Prefix with origin.
const ORIGIN   = (typeof window !== 'undefined' ? window.location.origin : '');
const LOGO_CGM = ORIGIN + '/pdf-logo-cloudgm.png';
const LOGO_RKZ = ORIGIN + '/pdf-logo-rakez.png';

const CONTACT = {
  sites:  ['cloudgm.com', 'cloudgm.business'],
  phone:  '+971 50 151 1917',
  emails: ['support@cloudgm.business', 'support@cloudgm.com'],
};

const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fmtDate = (iso, isAr) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(isAr ? 'ar-EG' : 'en-GB'); }
  catch { return iso; }
};

const buildFilenameBase = (activity, isAr) => {
  const raw = String(
    isAr ? (activity.activity_name_ar || activity.activity_name) : (activity.activity_name || activity.activity_name_ar) || activity.id || 'activity'
  ).trim();
  const clean = raw
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 80) || (isAr ? 'نشاط' : 'activity');
  const code = String(activity.activity_code || '').replace(/[^\u0600-\u06FFA-Za-z0-9-]/g, '');
  return isAr
    ? `نشاط_${clean}${code ? '_' + code : ''}`
    : `Activity_${clean}${code ? '_' + code : ''}`;
};

const buildHtml = (activity, isAr) => {
  const dir = isAr ? 'rtl' : 'ltr';
  const t   = (ar, en) => (isAr ? ar : en);
  const filenameBase = buildFilenameBase(activity, isAr);

  // Helper — render "—" if empty.
  const v = (x) => {
    const s = (x == null || x === '') ? '' : String(x);
    return s.trim() ? escapeHtml(s) : '—';
  };

  // Status colour.
  const isActive = String(activity.status || '').toLowerCase() === 'active';
  const statusLabel = isActive
    ? t('نشط', 'Active')
    : t('غير نشط', 'Inactive');
  const statusColor = isActive ? '#059669' : '#dc2626';

  // ── Dual-logo header ─────────────────────────────────────────
  const headerHtml = `
    <header class="doc-header">
      <div class="brand-strip">
        <img src="${LOGO_CGM}" alt="CloudGM FZ-LLC" class="brand-logo cgm" />
        <div class="brand-sep"></div>
        <img src="${LOGO_RKZ}" alt="RAKEZ" class="brand-logo rkz" />
      </div>
      <div class="doc-meta">
        <div class="doc-title">
          ${t('تفاصيل النشاط التجاري - راكز', 'Business Activity Details - RAKEZ')}
        </div>
        <div class="doc-ref">
          <span class="ref-label">${t('كود النشاط', 'Activity code')}:</span>
          <span class="ref-value">${v(activity.activity_code)}</span>
        </div>
        <div class="doc-date">
          <span class="ref-label">${t('تاريخ الطباعة', 'Printed on')}:</span>
          <span>${fmtDate(new Date().toISOString(), isAr)}</span>
        </div>
      </div>
    </header>`;

  // ── Status hero ─────────────────────────────────────────────
  const statusHeroHtml = `
    <section class="status-hero">
      <div class="status-block" style="--pill:${statusColor}">
        <div class="status-label">${t('حالة النشاط', 'Status')}</div>
        <div class="status-pill">${escapeHtml(statusLabel)}</div>
      </div>
      <div class="status-block">
        <div class="status-label">${t('الرقم التسلسلي', 'Sequence')}</div>
        <div class="status-value">${v(activity.sequence)}</div>
      </div>
      <div class="status-block">
        <div class="status-label">${t('نوع الرخصة', 'License type')}</div>
        <div class="status-value">${v(isAr ? (activity.license_type_ar || activity.license_type) : (activity.license_type || activity.license_type_ar))}</div>
      </div>
    </section>`;

  // ── Sections ────────────────────────────────────────────────
  const namesHtml = `
    <section class="section">
      <h2 class="section-title">${t('الأسماء والكود', 'Names & Code')}</h2>
      <div class="grid-2">
        <div class="kv"><span class="k">${t('الاسم بالعربية', 'Arabic name')}</span><span class="v" dir="rtl">${v(activity.activity_name_ar)}</span></div>
        <div class="kv"><span class="k">${t('الاسم بالإنجليزية', 'English name')}</span><span class="v" dir="ltr">${v(activity.activity_name)}</span></div>
        <div class="kv"><span class="k">${t('الكود', 'Code')}</span><span class="v mono">${v(activity.activity_code)}</span></div>
        <div class="kv"><span class="k">${t('الرقم التسلسلي', 'Sequence')}</span><span class="v mono">${v(activity.sequence)}</span></div>
      </div>
    </section>`;

  const licenseHtml = `
    <section class="section">
      <h2 class="section-title">${t('معلومات الرخصة', 'License information')}</h2>
      <div class="grid-2">
        <div class="kv"><span class="k">${t('نوع الرخصة (إنجليزي)', 'License type (EN)')}</span><span class="v">${v(activity.license_type)}</span></div>
        <div class="kv"><span class="k">${t('نوع الرخصة (عربي)', 'License type (AR)')}</span><span class="v">${v(activity.license_type_ar)}</span></div>
        <div class="kv"><span class="k">${t('المنطقة', 'Zone')}</span><span class="v">${v(activity.zone)}</span></div>
        <div class="kv"><span class="k">${t('الحالة', 'Status')}</span><span class="v" style="color:${statusColor};font-weight:800">${escapeHtml(statusLabel)}</span></div>
      </div>
    </section>`;

  const classificationHtml = `
    <section class="section">
      <h2 class="section-title">${t('التصنيف', 'Classification')}</h2>
      <div class="grid-2">
        <div class="kv"><span class="k">${t('مجموعة النشاط (إنجليزي)', 'Activity group (EN)')}</span><span class="v">${v(activity.activity_group)}</span></div>
        <div class="kv"><span class="k">${t('مجموعة النشاط (عربي)', 'Activity group (AR)')}</span><span class="v">${v(activity.activity_group_ar)}</span></div>
        <div class="kv"><span class="k">${t('قطاع GICS', 'GICS sector')}</span><span class="v">${v(activity.gics_business_sector)}</span></div>
        <div class="kv"><span class="k">${t('القطاع الفرعي', 'Subsector')}</span><span class="v">${v(activity.gics_subsector)}</span></div>
      </div>
    </section>`;

  const descHtml = `
    <section class="section">
      <h2 class="section-title">${t('الوصف', 'Description')}</h2>
      <div class="note-box">
        <div class="note-label">${t('الوصف بالعربية', 'Arabic description')}</div>
        <div dir="rtl">${v(activity.description_ar)}</div>
      </div>
      <div class="note-box internal">
        <div class="note-label">${t('الوصف بالإنجليزية', 'English description')}</div>
        <div dir="ltr">${v(activity.description_en)}</div>
      </div>
    </section>`;

  const reqHtml = `
    <section class="section">
      <h2 class="section-title">${t('الاشتراطات', 'Requirements')}</h2>
      <div class="grid-2">
        <div class="kv"><span class="k">${t('رأس المال الأدنى', 'Minimum share capital')}</span><span class="v">${v(activity.minimum_share_capital)}</span></div>
        <div class="kv"><span class="k">${t('السعر', 'Price')}</span><span class="v">${v(activity.activity_price)}</span></div>
      </div>
      ${activity.qualification_requirement ? `
        <div class="note-box">
          <div class="note-label">${t('اشتراطات النشاط', 'Activity requirements')}</div>
          <div>${v(activity.qualification_requirement)}</div>
        </div>` : ''}
      ${activity.documents_required ? `
        <div class="note-box internal">
          <div class="note-label">${t('المستندات المطلوبة', 'Required documents')}</div>
          <div>${v(activity.documents_required)}</div>
        </div>` : ''}
    </section>`;

  const extraHtml = `
    <section class="section">
      <h2 class="section-title">${t('معلومات إضافية', 'Additional information')}</h2>
      <div class="grid-2">
        <div class="kv"><span class="k">${t('تصنيف المخاطر HSE', 'HSE risk')}</span><span class="v">${v(activity.rakez_hse_risk)}</span></div>
        <div class="kv"><span class="k">${t('تقييم مخاطر الامتثال', 'Compliance risk')}</span><span class="v">${v(activity.compliance_risk_rating)}</span></div>
        <div class="kv"><span class="k">${t('نشاط خاص', 'Special activity')}</span><span class="v">${v(activity.is_special)}</span></div>
        <div class="kv"><span class="k">${t('غير مسموح للعمل المشترك', 'Not allowed coworking')}</span><span class="v">${v(activity.is_not_allowed_coworking)}</span></div>
      </div>
    </section>`;

  // ── Footer ───────────────────────────────────────────────────
  const footerHtml = `
    <div class="doc-footer">
      <div class="footer-bar"></div>
      <div class="footer-row">
        <span>🌐 ${CONTACT.sites.join(' • ')}</span>
        <span>📞 <span dir="ltr">${CONTACT.phone}</span></span>
        <span>✉ ${CONTACT.emails.join(' • ')}</span>
      </div>
      <div class="footer-line">CLOUD GM FZ-LLC — RAKEZ — ${t('طُبع في', 'Printed on')} ${fmtDate(new Date().toISOString(), isAr)}</div>
    </div>`;

  return `<!doctype html>
<html lang="${isAr ? 'ar' : 'en'}" dir="${dir}">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(filenameBase)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&family=Inter:wght@400;600;700&display=swap');

  :root {
    --blue-500: #3B9EDC;
    --blue-700: #1c6ea4;
    --orange-500: #F5A55A;
    --orange-700: #d97706;
    --ink-900: #0f172a;
    --ink-600: #334155;
    --ink-400: #64748b;
    --ink-200: #e2e8f0;
    --paper:   #ffffff;
    --tint:    #f8fafc;
    --brand-grad: linear-gradient(90deg, var(--blue-500) 0%, var(--orange-500) 100%);
  }

  @page {
    size: A4;
    /* Top margin bumped so page-2 content isn't glued to the top edge
       when a section wraps across pages. */
    margin: 26mm 14mm 28mm 14mm;
  }
  html, body {
    margin: 0; padding: 0;
    background: var(--paper);
    color: var(--ink-900);
    font-family: ${isAr ? "'Cairo'" : "'Inter'"}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    font-size: 12px;
    line-height: 1.55;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ── Header w/ dual-logo strip ──────────────────────────── */
  .doc-header {
    display: flex; align-items: center; gap: 20px;
    padding: 16px 18px;
    border: 1px solid var(--ink-200);
    border-radius: 14px;
    background: var(--tint);
    position: relative;
    overflow: hidden;
  }
  .doc-header::before {
    content: '';
    position: absolute; inset: 0 0 auto 0; height: 6px;
    background: var(--brand-grad);
  }
  .brand-strip {
    display: flex; align-items: center; gap: 14px;
    flex-shrink: 0;
  }
  .brand-logo { display: block; }
  .brand-logo.cgm { height: 68px; width: auto; }
  .brand-logo.rkz { height: 62px; width: auto; }
  .brand-sep {
    width: 1px; height: 46px;
    background: linear-gradient(180deg, transparent 0%, var(--ink-200) 20%, var(--ink-200) 80%, transparent 100%);
  }
  .doc-meta { flex: 1; }
  .doc-title {
    font-size: 18px; font-weight: 800;
    color: var(--ink-900);
    letter-spacing: 0.2px;
    margin-bottom: 6px;
  }
  .doc-ref, .doc-date { font-size: 11px; color: var(--ink-600); }
  .doc-ref .ref-value { font-family: 'JetBrains Mono', 'Menlo', monospace; color: var(--blue-700); font-weight: 700; margin-inline-start: 6px; }
  .doc-date { margin-top: 2px; }
  .ref-label { color: var(--ink-400); font-weight: 600; }

  /* ── Status hero ───────────────────────────────────────────── */
  .status-hero {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
    margin: 14px 0 6px 0;
  }
  .status-block {
    padding: 12px 14px;
    background: var(--tint);
    border: 1px solid var(--ink-200);
    border-radius: 10px;
  }
  .status-block .status-label {
    font-size: 10px; text-transform: uppercase;
    letter-spacing: 0.6px; color: var(--ink-400); font-weight: 700;
    margin-bottom: 4px;
  }
  .status-value { font-size: 13px; font-weight: 700; color: var(--ink-900); }
  .status-pill {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 999px;
    background: var(--pill, var(--blue-500));
    color: #fff;
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.3px;
  }

  /* ── Section ───────────────────────────────────────────────── */
  .section {
    margin-top: 14px;
    /* Keep the section title + first row together. If the whole section
       has to break, add a 6mm safety strut at the top of the new page. */
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .section + .section { padding-top: 4mm; }
  .section-title {
    font-size: 12px;
    font-weight: 800;
    margin: 0 0 8px 0;
    padding: 6px 12px;
    background: var(--brand-grad);
    color: #fff;
    border-radius: 8px;
    display: inline-block;
  }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; }
  .kv {
    display: flex; align-items: center; justify-content: space-between;
    padding: 6px 10px;
    background: #fff;
    border: 1px solid var(--ink-200);
    border-radius: 8px;
    gap: 8px;
  }
  .kv .k { color: var(--ink-400); font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
  .kv .v { color: var(--ink-900); font-weight: 700; font-size: 12px; text-align: end; word-break: break-word; }
  .mono { font-family: 'JetBrains Mono', 'Menlo', monospace; font-size: 11px; }

  .note-box {
    margin-top: 8px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid var(--ink-200);
    border-inline-start: 4px solid var(--blue-500);
    border-radius: 8px;
    white-space: pre-wrap;
  }
  .note-box .note-label { font-size: 10px; color: var(--ink-400); font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
  .note-box.internal { border-inline-start-color: var(--orange-500); background: #fffdf7; }

  /* ── Footer (fixed on every page) ──────────────────────────── */
  .doc-footer {
    position: fixed;
    bottom: 6mm;
    inset-inline-start: 14mm;
    inset-inline-end: 14mm;
    text-align: center;
  }
  .footer-bar { height: 4px; background: var(--brand-grad); border-radius: 4px; margin-bottom: 6px; }
  .footer-row {
    display: flex; justify-content: space-around; flex-wrap: wrap;
    gap: 8px;
    font-size: 9.5px;
    color: var(--ink-600);
    font-weight: 600;
  }
  .footer-line {
    font-size: 8.5px;
    color: var(--ink-400);
    margin-top: 4px;
    letter-spacing: 0.3px;
  }

  /* ── Print controls (screen only) ──────────────────────────── */
  .print-bar {
    position: sticky; top: 0; z-index: 10;
    padding: 10px 14px;
    background: var(--ink-900);
    color: #fff;
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px;
    box-shadow: 0 4px 10px rgba(0,0,0,0.08);
  }
  .print-bar button {
    background: var(--brand-grad);
    color: #fff; font-weight: 800;
    border: none; padding: 8px 18px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
  }
  @media print {
    .print-bar { display: none; }
    body { background: var(--paper); }
    .doc-wrapper { padding: 0; }
  }

  .doc-wrapper { padding: 14px 20px 40mm 20px; max-width: 210mm; margin: 0 auto; }
</style>
</head>
<body>

  <div class="print-bar">
    <span>${t('معاينة قبل الطباعة', 'Print preview')} — ${escapeHtml(activity.activity_code || activity.id || '')}</span>
    <button onclick="window.print()">${t('🖨️ طباعة', '🖨️ Print')}</button>
  </div>

  <div class="doc-wrapper">
    ${headerHtml}
    ${statusHeroHtml}
    ${namesHtml}
    ${licenseHtml}
    ${classificationHtml}
    ${descHtml}
    ${reqHtml}
    ${extraHtml}
  </div>

  ${footerHtml}

  <script>
    (function(){
      var imgs = document.querySelectorAll('.brand-logo');
      var loaded = 0, total = imgs.length;
      var fire = function(){ setTimeout(function(){ window.focus(); window.print(); }, 500); };
      if (total === 0) return fire();
      imgs.forEach(function(img){
        var done = function(){ loaded += 1; if (loaded >= total) fire(); };
        if (img.complete) return done();
        img.addEventListener('load',  done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    })();
  <\/script>
</body>
</html>`;
};

/**
 * Public entry point — opens a new tab with the print-ready HTML.
 * opts.language = 'ar' | 'en' (defaults to Arabic).
 */
export const printRakezActivity = (activity, opts = {}) => {
  if (!activity) return;
  const urlLang = (typeof window !== 'undefined')
    ? new URLSearchParams(window.location.search).get('lang')
    : null;
  const language = (opts.language || urlLang || 'ar').toLowerCase();
  const isAr = language.startsWith('ar');
  const html = buildHtml(activity, isAr);

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const win  = window.open(url, '_blank');
  if (!win) {
    window.location.href = url;
    return;
  }
  setTimeout(() => { URL.revokeObjectURL(url); }, 60_000);
};
